import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Desktop,
  LargeTablet,
  SmallTablet,
  Mobile,
} from "../MediaQuery/QueryPageMediaQuery.js";
import styled from "styled-components";
import Banner from "../Components/Banner.jsx";
import LogBar from "../Components/LogBar.jsx";
import Result from "../Components/Result.jsx";
import BannerMobile from "../Components/BannerMobile.jsx";
import HeightInterval from "../Components/HeightInterval.jsx";
import ToggleDropdown from "../Components/ToggleDropdown.jsx";
import ModalUnauthorizedUser from "../Components/Modal/ModalUnauthorizedUser.jsx";
import useStore from "../store.js";
import { useNavigate } from "react-router-dom";
import ModaRefreshlDetected from "../Components/Modal/ModaRefreshlDetected.jsx";
import Stop from "../Components/Stop";
import { Helmet } from "react-helmet-async";
import SnackbarComponent from "../Components/SnackBar.jsx";
import snackBar_delete from "../img/snackBar_delete.png";
import snackBar_copy from "../img/snackBar_copy.png";
import SpinnerSSE from "../Components/SpinnerSSE.jsx";
import sseFinish from "../img/sse_finish.png";
import ModalAskError from "../Components/Modal/ModalAskError.jsx";
import ModalUserFeedback from "../Components/Modal/ModalUserFeedback.jsx";
import warning from "../img/user_feedback_warning.png";
import SSEProgressStack from "../Components/SSEProgressStack.jsx";
import { shouldShowSearchComplete } from "../utils/searchProgress.js";

// 특정 로그 조회
// 질문결과 페이지
const Query = () => {
  const [showUnauthorizedModal, setShowUnauthorizedModal] = useState(false);
  const [showAskErrorModal, setShowAskErrorModal] = useState(false);
  const [deviceType, setDeviceType] = useState('');
  const { status } = useParams();
  const navigate = useNavigate();

  const {
    updateUserInfo,
    updateUserLaw,
    isRefreshDetected,
    setRefreshDetected,
    initializeStore,
    fetchLogs,
    logs,
    showDeleteHistorySnackbar,
    showCopyCaseSnackbar,
    cleanupSSE,
    sseEventName,
    sseDescription,
    textValue,
    setHasAttemptedQuestion,
    logId,
    queryCompleteData,
  } = useStore();

  const initialRender = useRef(true);
  const [showUserFeedbackModal, setShowUserFeedbackModal] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const showCompleteText = shouldShowSearchComplete({
    status,
    sseEventName,
    logId,
    queryCompleteData,
  });

  // 사용자 정보 업데이트 
  useEffect(() => {
    const fetchData = async () => {
      try {
        await updateUserInfo(setShowUnauthorizedModal);
        await updateUserLaw();
      } catch (error) {
        console.error("Failed to update user info or law history:", error);
      }
    };

    fetchData();
  }, []);

  // 질문 내역 조회
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs, status]);

  // 비로그인 상태 체크
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }

    if (!showUnauthorizedModal) {
      navigate("/");
    }
  }, [showUnauthorizedModal]);

  // 로컬 스토리지에 상태 동기화(영구 저장)하여 새로고침을 해도 상태값이 초기화되지 않도록 조치함
  useEffect(() => {
    initializeStore();
  }, []);

  // SSE 이벤트 처리
  useEffect(() => {
    if (sseEventName === 'Error') {
      // Error 이벤트가 발생하면 오류 모달을 표시
      setShowAskErrorModal(true);
    }
  }, [sseEventName]);

  // 새로고침 감지 함수 (검색 질문 중에는 팝업 없이 status/stream으로 복구)
  const handleBeforeUnload = (event) => {
    // Chat에서 시작된 판례 검색인지 확인
    const fromChatSearch = sessionStorage.getItem("fromChatSearch");
    
    if (status === "waiting" && !fromChatSearch) {
      // 검색 질문 중에는 새로고침 감지 팝업 없이 status/stream으로 복구하도록 변경
      // 따라서 preventDefault()와 setRefreshDetected()를 호출하지 않음
      return;
    }
  };

  // 새로고침 감지 이벤트 등록 (컴포넌트 마운트 시 한 번만)
  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      // 컴포넌트 언마운트 시 fromChatSearch 플래그 정리
      sessionStorage.removeItem("fromChatSearch");
    };
  }, []);

  // ModaRefreshlDetected 모달 닫기 및 플래그 초기화 + 메인 이동
  const handleCloseRefreshModal = () => {
    setRefreshDetected(false); 
    navigate("/main");
  };

   // 질문 오류시 알림 모달창 
  const handleCloseAskErrorModal = () => {
    // 오류 안내를 닫아도 현재 읽고 있는 저장 결과는 유지합니다.
    setShowAskErrorModal(false); // 모달 닫기
    setHasAttemptedQuestion();  // 질문 시도가 있었음을 store에 기록
    cleanupSSE(); // 에러 팝업 띄운 후 event 종류를 undefined로 초기화
    // 대기 화면에서 실패한 경우에만 원래 입력으로 돌아갑니다.
    if (status === "waiting") {
      navigate(`/main`, { state: { textValue } });
    }
  };

  // 현재 디바이스 타입을 결정하는 로직
  const getDeviceType = () => {
    const width = window.innerWidth;
    if (width >= 1141) return 'desktop';
    if (width >= 745) return 'largeTablet';
    if (width >= 481) return 'smallTablet';
    return 'mobile';
  };

  // 디바이스 타입 초기화 및 리사이즈 이벤트 처리
  useEffect(() => {
    const handleResize = () => {
      setDeviceType(getDeviceType());
    };

    // 초기 디바이스 타입 설정
    handleResize();

    // resize 이벤트 리스너 등록
    window.addEventListener('resize', handleResize);

    // cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const renderMainContent = () => {
    // 질문 응답이 오게돼서 /waiting에서 /log_id로 넘어가게 되면 SSEProgressStack 컴포넌트는 언마운트됨
    // 왜냐하면, status가 "waiting"일 때만 <SSEProgressStack /> 컴포넌트를 렌더링하기 때문
    return (
      <>
        <Result resultId={status} />
        {status === "waiting" && <SSEProgressStack showCompleteText={showCompleteText} />}
      </>
    );
  };

  // 디바이스 타입에 따른 컨텐츠 렌더링
  const renderDeviceContent = () => {
    switch (deviceType) {
      case 'desktop':
        return (
          <Wrapper>
            <LogBar />
            <MainWrapper>
              <Banner />
              {renderMainContent()}
            </MainWrapper>
          </Wrapper>
        );
      case 'largeTablet':
        return (
          <>
            <HeightInterval height={21} />
            <WrapperMobile>
              <BannerMobile />
            </WrapperMobile>
            <HeightInterval height={22} />
            {renderMainContent()}
          </>
        );
      case 'smallTablet':
        return (
          <>
            <HeightInterval height={21} />
            <WrapperMobile>
              <BannerMobile />
            </WrapperMobile>
            <HeightInterval height={22} />
            {renderMainContent()}
          </>
        );
      case 'mobile':
        return (
          <>
            <FixedWrapper>
              <HeightInterval height={20} />
              <WrapperMobile>
                <BannerMobile />
              </WrapperMobile>
              <HeightInterval height={24} />
              <ToggleDropdown id={status} />
            </FixedWrapper>
            <HeightInterval height={40} />
            <WrapperMobile>
              {renderMainContent()}
            </WrapperMobile>
          </>
        );
      default:
        return null;
    }
  };

  return (
    // <Stop />
    <>
      <Helmet>
        <title>내 상황에 딱 맞는 판례 검색하기 | 로우로우</title>
        <meta
          name="description"
          content="나의 상황과 딱맞는 판례를 AI가 엄선했어요. 문제해결에 필요한 판례로 나 자신을 보호하세요."
        />
        <meta
          name="keywords"
          content="변호사, 변호사 추천, 선임비용, 무료법률상담, 전화법률상담, 법률상담, 법, 법률, 사기, 이혼, 범죄, 채무"
        />
      </Helmet>
      <ModaRefreshlDetected isOpen={isRefreshDetected} onClose={handleCloseRefreshModal} />
      {showAskErrorModal && (
        <ModalAskError 
          isOpen={showAskErrorModal} 
          onClose={handleCloseAskErrorModal}
        />
      )}

      {/* Desktop 이외의 디바이스에서만 스낵바와 나머지 렌더링 */}
      {deviceType !== 'desktop' && (
        <>
          <SnackbarComponent
            visible={showDeleteHistorySnackbar}
            src={snackBar_delete}
            top={72}
          />
          <SnackbarComponent
            visible={showCopyCaseSnackbar}
            src={snackBar_copy}
            top={500}
          />
        </>
      )}

      {/* 디바이스 타입이 안정화될 때까지 로딩 표시 */}
      {renderDeviceContent()}

      {showUnauthorizedModal && (
        <ModalUnauthorizedUser
          isOpen={showUnauthorizedModal}
          onClose={() => setShowUnauthorizedModal(false)}
        />
      )}

      <div style={{ position: "relative", display: "inline-block" }}>
        <ModalUserFeedback
          isOpen={showUserFeedbackModal}
          onClose={() => setShowUserFeedbackModal(false)}
          onSubmit={() => setShowUserFeedbackModal(false)}
          onShowWarning={() => {
            setShowWarning(true);
            setTimeout(() => setShowWarning(false), 2000);
          }}
        />
        {/* (25.06.23) 이질어(ModalAskFormSuggestion.jsx) 확인/취소 버튼의 앞을 막는 오류가 있어, 일단은 주석처리) */}
        {/* <WarningBox show={showWarning}>
          <ContentWrapper>
            <StyledWarningIcon src={warning} alt="경고" />
            <StyledWarningText>
              만족도 옵션 중 하나를 선택해야 제출할 수 있습니다.
            </StyledWarningText>
          </ContentWrapper>
        </WarningBox> */}
      </div>
    </>
  );
};

const Wrapper = styled.div`
  display: flex; /* 가로정렬 */
`;

const WrapperMobile = styled.div`
  width: auto;

  /* 배너 좌우 margin */
  @media (max-width: 1140px) {
    margin: 0px 24px;
  }

  @media (max-width: 744px) {
    margin: 0px 32px;
  }

  @media (max-width: 480px) {
    margin: 0px 24px;
  }
`;

const MainWrapper = styled.div`
  width: calc(100% - 312px); // LogBar의 너비 312px 이외 영역
  margin-left: 312px; /* LogBar의 영역 logBarMinWidth 20% */
`;

const FixedWrapper = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 100; /* 다른 콘텐츠 위에 표시되도록 z-index 설정 */
  background-color: white; /* 배경 색상 설정 (필요 시) */
`;

const WaitingSituationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const WaitingBox = styled.div`
  width: 680px;
  height: 71px;
  border-radius: 24px;
  background: #F9F9F9;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-top: 24px;

  @media (max-width: 744px) and (min-width: 481px) {
    width: calc(100% - 64px);
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const WaitingText = styled.span`
  color: rgba(0, 0, 0, 0.60);
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 26px;
  letter-spacing: -0.32px;

  @media (max-width: 480px) {
    font-size: 13px;
    line-height: 20px; 
    letter-spacing: -0.26px;
  } 
`;

const FinishIcon = styled.img`
  width: 20px;
  height: 20px;

  @media (max-width: 480px) {
    width: 16px;
    height: 16px;
  }
`;

const SpinnerTextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  margin-left: 32px;
`;

const WarningBox = styled.div`
  position: fixed;
  left: 50%;
  top: calc(50% + 255px); // 모달 중앙 아래로 240px(모달 높이/2 + 12px 등) 조정
  transform: translateX(-50%) translateY(${props => props.show ? '0' : '10px'});
  width: 400px;
  height: 44px;
  border-radius: 16px;
  background: #FFF;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid transparent;
  gap: 0;
  opacity: ${props => props.show ? 1 : 0};
  transition: opacity 300ms ease-out, transform 300ms ease-out;
  z-index: 1000;

  @media (max-width: 430px) {
    width: 312px;
    top: calc(50% + 225px); // 모바일 모달 하단 + 12px (모달 높이에 맞게 조정)
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-left: 16px;
  width: 100%;
  height: 100%;
`;

const StyledWarningIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-right: 8px;
`;

const StyledWarningText = styled.span`
  color: rgba(0, 0, 0, 0.60);
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; 
  letter-spacing: -0.28px;

  @media (max-width: 430px) {
    font-size: 12px;
    letter-spacing: -0.24px;
  }
`;


export default Query;
